import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Loader2, X, AlertCircle, Calendar, DollarSign, FileText, Pencil, Check } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

const STATUS_STYLE = {
  applied: "bg-blue-50 text-blue-700",
  awarded: "bg-emerald-50 text-emerald-700",
  active: "bg-njas-sage-tint text-njas-sage-hover",
  acquitted: "bg-gray-100 text-gray-700",
  closed: "bg-gray-100 text-gray-500",
  declined: "bg-red-50 text-red-700",
};

const MILESTONE_STYLE = {
  pending: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-50 text-blue-700",
  completed: "bg-emerald-50 text-emerald-700",
  overdue: "bg-red-50 text-red-700",
};

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + "T00:00:00"); d.setHours(0, 0, 0, 0);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
}
function money(n, curr = "AUD") {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: curr, maximumFractionDigits: 0 }).format(n);
}

export default function Grants() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/grants");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (tab === "all") return items;
    if (tab === "active") return items.filter((g) => ["active", "awarded"].includes(g.status));
    if (tab === "closed") return items.filter((g) => ["acquitted", "closed", "declined"].includes(g.status));
    return items;
  }, [items, tab]);

  const totals = useMemo(() => {
    const active = items.filter((g) => ["active", "awarded"].includes(g.status));
    const totalAwarded = active.reduce((s, g) => s + (g.amount_total || 0), 0);
    const totalSpent = items.reduce((s, g) => s + (g.actual_spend || 0), 0);
    const upcomingReports = items
      .filter((g) => g.next_reporting_date && daysUntil(g.next_reporting_date) != null && daysUntil(g.next_reporting_date) <= 30 && daysUntil(g.next_reporting_date) >= 0)
      .length;
    return { activeCount: active.length, totalAwarded, totalSpent, upcomingReports };
  }, [items]);

  const del = async (g) => {
    if (!window.confirm(`Delete grant "${g.name}"? This cannot be undone.`)) return;
    setBusy(g.id);
    try {
      await api.delete(`/grants/${g.id}`);
      toast.success("Grant deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="fade-in" data-testid="grants-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Compliance</div>
          <h2 className="font-serif text-3xl text-njas-navy">Grants Register</h2>
          <p className="text-sm text-njas-muted mt-1">Track all funding, milestones, reporting deadlines and acquittals.</p>
        </div>
        <button
          data-testid="add-grant-btn"
          onClick={() => setEditing({ status: "applied", currency: "AUD", milestones: [] })}
          className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-4 py-2 text-sm hover:bg-njas-navy-hover"
        >
          <Plus className="w-4 h-4" /> Add grant
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FileText} tint="bg-blue-50 text-blue-700" label="Active grants" value={totals.activeCount} />
        <StatCard icon={DollarSign} tint="bg-emerald-50 text-emerald-700" label="Awarded (active)" value={money(totals.totalAwarded)} />
        <StatCard icon={DollarSign} tint="bg-njas-sage-tint text-njas-sage-hover" label="Total spent" value={money(totals.totalSpent)} />
        <StatCard icon={AlertCircle} tint={totals.upcomingReports ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"} label="Reports due (30d)" value={totals.upcomingReports} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 text-sm">
        {[
          { k: "all", l: "All" },
          { k: "active", l: "Active" },
          { k: "closed", l: "Closed / acquitted" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`grants-tab-${t.k}`}
            className={`px-3 py-1.5 rounded-full ${tab === t.k ? "bg-njas-navy text-white" : "bg-white border border-[var(--njas-border)] text-njas-muted hover:text-njas-navy"}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-njas-muted">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--njas-border)] p-8 text-center text-sm text-njas-muted">
          No grants yet. Click <strong>Add grant</strong> to start your register.
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((g) => {
          const reportDays = daysUntil(g.next_reporting_date);
          const isDueSoon = reportDays !== null && reportDays >= 0 && reportDays <= 14;
          const isOverdue = reportDays !== null && reportDays < 0 && !["closed", "acquitted", "declined"].includes(g.status);
          const spendPct = g.amount_total ? Math.min(100, Math.round(((g.actual_spend || 0) / g.amount_total) * 100)) : 0;
          return (
            <div key={g.id} data-testid={`grant-row-${g.id}`} className="rounded-xl bg-white border border-[var(--njas-border)] p-5 hover:border-njas-sage transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-xl text-njas-navy">{g.name}</div>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${STATUS_STYLE[g.status] || "bg-gray-100 text-gray-700"}`}>{g.status}</span>
                    {isOverdue && <span className="inline-flex items-center gap-1 text-[10px] text-red-700 font-semibold"><AlertCircle className="w-3 h-3" /> report overdue</span>}
                    {isDueSoon && !isOverdue && <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 font-semibold"><AlertCircle className="w-3 h-3" /> due in {reportDays}d</span>}
                  </div>
                  <div className="text-sm text-njas-muted mt-1">{g.funder}</div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-njas-muted">
                    <span>Amount: <strong className="text-njas-navy">{money(g.amount_total, g.currency)}</strong></span>
                    {g.start_date && <span>Period: {fmtDate(g.start_date)} – {fmtDate(g.end_date)}</span>}
                    {g.next_reporting_date && <span>Next report: <strong className={isOverdue ? "text-red-700" : isDueSoon ? "text-amber-700" : "text-njas-navy"}>{fmtDate(g.next_reporting_date)}</strong></span>}
                    {g.contact_person && <span>Contact: {g.contact_person}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(g)} data-testid={`grant-edit-${g.id}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[var(--njas-border)] hover:bg-njas-bg-alt text-njas-navy">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => del(g)} disabled={busy === g.id} data-testid={`grant-delete-${g.id}`}
                    className="p-2 text-njas-muted hover:text-red-600 rounded-lg" aria-label="Delete">
                    {busy === g.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Acquittal bar */}
              {g.amount_total > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-njas-muted">Spent {money(g.actual_spend, g.currency)} of {money(g.approved_budget || g.amount_total, g.currency)}</span>
                    <span className="text-njas-navy font-medium">{spendPct}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-njas-bg-alt overflow-hidden">
                    <div className={`h-full ${spendPct >= 100 ? "bg-red-500" : spendPct >= 80 ? "bg-amber-500" : "bg-njas-sage"}`} style={{ width: `${Math.min(100, spendPct)}%` }} />
                  </div>
                </div>
              )}

              {/* Milestones summary */}
              {(g.milestones || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-[var(--njas-border)]">
                  <div className="text-[10px] uppercase tracking-wider text-njas-muted mb-2">Milestones ({g.milestones.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.milestones.slice(0, 6).map((m, i) => (
                      <span key={m.id || i} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded ${MILESTONE_STYLE[m.status] || "bg-gray-100"}`}>
                        {m.status === "completed" && <Check className="w-2.5 h-2.5" />}
                        {m.title}{m.due_date ? ` · ${fmtDate(m.due_date)}` : ""}
                      </span>
                    ))}
                    {g.milestones.length > 6 && <span className="text-[11px] text-njas-muted">+{g.milestones.length - 6} more</span>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <GrantFormModal
          grant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, tint, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-[var(--njas-border)] p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></div>
      <div className="mt-3 font-serif text-2xl text-njas-navy">{value}</div>
      <div className="text-xs text-njas-muted">{label}</div>
    </div>
  );
}

function GrantFormModal({ grant, onClose, onSaved }) {
  const isNew = !grant?.id;
  const [f, setF] = useState({
    name: grant?.name || "",
    funder: grant?.funder || "",
    amount_total: grant?.amount_total ?? "",
    currency: grant?.currency || "AUD",
    status: grant?.status || "applied",
    applied_date: grant?.applied_date || "",
    start_date: grant?.start_date || "",
    end_date: grant?.end_date || "",
    next_reporting_date: grant?.next_reporting_date || "",
    contact_person: grant?.contact_person || "",
    contact_email: grant?.contact_email || "",
    purpose: grant?.purpose || "",
    kpis: grant?.kpis || "",
    notes: grant?.notes || "",
    approved_budget: grant?.approved_budget ?? "",
    actual_spend: grant?.actual_spend ?? "",
  });
  const [milestones, setMilestones] = useState(grant?.milestones || []);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const addMilestone = () => setMilestones([...milestones, { title: "", due_date: "", status: "pending", notes: "" }]);
  const updateMilestone = (i, patch) => setMilestones(milestones.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const removeMilestone = (i) => setMilestones(milestones.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!f.name.trim() || !f.funder.trim()) { toast.error("Grant name and funder are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        amount_total: f.amount_total === "" ? null : Number(f.amount_total),
        approved_budget: f.approved_budget === "" ? null : Number(f.approved_budget),
        actual_spend: f.actual_spend === "" ? null : Number(f.actual_spend),
        milestones: milestones.filter((m) => m.title.trim()),
      };
      if (isNew) {
        await api.post("/grants", payload);
        toast.success("Grant added");
      } else {
        await api.patch(`/grants/${grant.id}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose} data-testid="grant-form-modal">
      <div className="min-h-screen bg-white p-6 sm:p-10" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="eyebrow text-njas-sage-hover text-[10px]">Grants</div>
              <h3 className="font-serif text-3xl text-njas-navy">{isNew ? "Add grant" : "Edit grant"}</h3>
              <p className="text-sm text-njas-muted mt-1">All fields feed the Register, deadline alerts and acquittal reporting.</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-njas-bg-alt text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
          </div>

          <div className="space-y-8">
            {/* Basics */}
            <Section title="Basics">
              <Field label="Grant name" required><input value={f.name} onChange={upd("name")} className={inputCls} data-testid="grant-form-name" /></Field>
              <Field label="Funder / grantor" required><input value={f.funder} onChange={upd("funder")} className={inputCls} data-testid="grant-form-funder" placeholder="e.g. Dept of Communities & Justice" /></Field>
              <Field label="Status">
                <select value={f.status} onChange={upd("status")} className={inputCls} data-testid="grant-form-status">
                  <option value="applied">Applied</option>
                  <option value="awarded">Awarded</option>
                  <option value="active">Active</option>
                  <option value="acquitted">Acquitted</option>
                  <option value="closed">Closed</option>
                  <option value="declined">Declined</option>
                </select>
              </Field>
              <Field label="Currency">
                <select value={f.currency} onChange={upd("currency")} className={inputCls}>
                  <option value="AUD">AUD</option><option value="USD">USD</option><option value="NZD">NZD</option>
                </select>
              </Field>
              <Field label="Grant amount"><input type="number" step="0.01" value={f.amount_total} onChange={upd("amount_total")} className={inputCls} data-testid="grant-form-amount" /></Field>
              <Field label="Purpose / project"><input value={f.purpose} onChange={upd("purpose")} className={inputCls} placeholder="What the grant is funding" /></Field>
            </Section>

            {/* Dates */}
            <Section title="Dates & deadlines">
              <Field label="Date applied"><input type="date" value={f.applied_date || ""} onChange={upd("applied_date")} className={inputCls} /></Field>
              <Field label="Project start"><input type="date" value={f.start_date || ""} onChange={upd("start_date")} className={inputCls} /></Field>
              <Field label="Project end"><input type="date" value={f.end_date || ""} onChange={upd("end_date")} className={inputCls} /></Field>
              <Field label="Next reporting date"><input type="date" value={f.next_reporting_date || ""} onChange={upd("next_reporting_date")} className={inputCls} data-testid="grant-form-next-report" /></Field>
            </Section>

            {/* Contact */}
            <Section title="Grantor contact">
              <Field label="Contact person"><input value={f.contact_person} onChange={upd("contact_person")} className={inputCls} /></Field>
              <Field label="Contact email"><input type="email" value={f.contact_email} onChange={upd("contact_email")} className={inputCls} /></Field>
            </Section>

            {/* Acquittal */}
            <Section title="Acquittal — budget vs actuals">
              <Field label="Approved budget"><input type="number" step="0.01" value={f.approved_budget} onChange={upd("approved_budget")} className={inputCls} data-testid="grant-form-approved-budget" /></Field>
              <Field label="Actual spend to date"><input type="number" step="0.01" value={f.actual_spend} onChange={upd("actual_spend")} className={inputCls} data-testid="grant-form-actual-spend" /></Field>
              <Field label="KPIs / performance metrics" full>
                <textarea rows={3} value={f.kpis} onChange={upd("kpis")} className={inputCls}
                  placeholder="e.g. 50 meals served weekly; 20 clients supported; 4 workshops delivered by end of quarter" data-testid="grant-form-kpis" />
              </Field>
            </Section>

            {/* Milestones */}
            <Section title="Milestones & reporting dates" fullCols>
              <div className="col-span-2 space-y-3">
                {milestones.length === 0 && (
                  <div className="text-sm text-njas-muted italic">No milestones yet. Add reporting checkpoints to track progress.</div>
                )}
                {milestones.map((m, i) => (
                  <div key={i} className="grid sm:grid-cols-12 gap-2 items-start rounded-lg border border-[var(--njas-border)] p-3 bg-njas-bg-alt/30">
                    <input value={m.title} onChange={(e) => updateMilestone(i, { title: e.target.value })} placeholder="Milestone title" className={`${inputCls} sm:col-span-4`} data-testid={`milestone-title-${i}`} />
                    <input type="date" value={m.due_date || ""} onChange={(e) => updateMilestone(i, { due_date: e.target.value })} className={`${inputCls} sm:col-span-3`} data-testid={`milestone-date-${i}`} />
                    <select value={m.status} onChange={(e) => updateMilestone(i, { status: e.target.value })} className={`${inputCls} sm:col-span-2`} data-testid={`milestone-status-${i}`}>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In progress</option>
                      <option value="completed">Completed</option>
                      <option value="overdue">Overdue</option>
                    </select>
                    <input value={m.notes || ""} onChange={(e) => updateMilestone(i, { notes: e.target.value })} placeholder="Notes" className={`${inputCls} sm:col-span-2`} />
                    <button onClick={() => removeMilestone(i)} className="sm:col-span-1 text-njas-muted hover:text-red-600 p-1.5" data-testid={`milestone-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={addMilestone} data-testid="add-milestone-btn" className="inline-flex items-center gap-2 text-sm text-njas-navy hover:text-njas-navy-hover">
                  <Plus className="w-4 h-4" /> Add milestone
                </button>
              </div>
            </Section>

            {/* Notes */}
            <Section title="Notes" fullCols>
              <textarea rows={4} value={f.notes} onChange={upd("notes")} className={`${inputCls} sm:col-span-2`}
                placeholder="Internal notes: conditions, restrictions, reminders…" data-testid="grant-form-notes" />
            </Section>
          </div>

          <div className="sticky bottom-0 -mx-6 sm:-mx-10 mt-8 px-6 sm:px-10 py-4 bg-white border-t border-[var(--njas-border)] flex justify-end gap-2">
            <button onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="grant-form-save"
              className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm font-medium hover:bg-njas-navy-hover disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save grant"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, fullCols }) {
  return (
    <div>
      <div className="njas-condensed tracking-[0.16em] text-[11px] text-njas-sage-hover font-semibold uppercase mb-3">{title}</div>
      <div className={`grid gap-4 ${fullCols ? "grid-cols-1" : "sm:grid-cols-2"}`}>{children}</div>
    </div>
  );
}
function Field({ label, required, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs text-njas-muted uppercase tracking-wider block mb-1">{label}{required && <span className="text-red-500 ml-1">*</span>}</span>
      {children}
    </label>
  );
}
